import { Form, Switch, Tooltip } from "antd"
import React from "react"

import { PERMISSION_DEPENDENCIES } from "../helpers/dependencies.ts"
import { type PermissionsMode } from "../types.ts"
import type {
  AnyPermissionKey,
  PermissionDisplayMap,
  PermissionFeatures
} from "../types/permissions.types.ts"

type PermissionsActionSwitchProps = {
  parentKey: keyof PermissionFeatures
  displayMap?: PermissionDisplayMap | null
  subKey: string
  actionKey: string
  value: boolean
  isUpdating?: boolean
} & PermissionsMode

const getDepLabel = (depKey: string): string =>
  depKey
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")

export const PermissionsActionSwitch = (
  props: PermissionsActionSwitchProps
) => {
  const deps = PERMISSION_DEPENDENCIES[props.actionKey as AnyPermissionKey]
  const tooltipTitle = deps?.length
    ? `Requires: ${deps.map((depKey) => props?.displayMap?.[depKey]?.name || getDepLabel(depKey)).join(", ")}`
    : undefined

  if (props.asForm) {
    return (
      <Tooltip title={tooltipTitle}>
        {/* span needed: Form.Item with noStyle doesn't forward hover events to Tooltip */}
        <span className="inline-flex justify-center">
          <Form.Item
            name={[props.parentKey, props.subKey, props.actionKey]}
            valuePropName="checked"
            initialValue={props.value}
            noStyle
          >
            <Switch size="small" />
          </Form.Item>
        </span>
      </Tooltip>
    )
  }

  const { parentKey, subKey, actionKey, value, isUpdating, onItemChange } =
    props

  const handleChange = (v: boolean) =>
    onItemChange(
      parentKey,
      subKey as keyof PermissionFeatures[typeof parentKey],
      actionKey as keyof PermissionFeatures[typeof parentKey][keyof PermissionFeatures[typeof parentKey]],
      v
    )

  return (
    <Tooltip title={tooltipTitle}>
      <span className="inline-flex justify-center">
        <Switch
          size="small"
          checked={value}
          onChange={handleChange}
          disabled={isUpdating}
        />
      </span>
    </Tooltip>
  )
}
